import { gerstnerHeight, gerstnerNormal } from "./gerstner";
import type { ProbeResult, Vec2, WaveParams } from "./types";

/**
 * Map a pixel in the drawing buffer to a world coordinate. This mapping is
 * shared conceptually with the fragment shader (which flips gl_FragCoord.y so
 * that both use a top-left origin), guaranteeing the probe reads the same point
 * the shader draws.
 *
 * The world is centered at the origin; `extent` world units span the buffer
 * width, and height is scaled by the buffer aspect ratio.
 */
export function pixelToWorld(
  px: number,
  py: number,
  bufferWidth: number,
  bufferHeight: number,
  extent: number,
): Vec2 {
  const worldWidth = extent;
  const worldHeight = bufferHeight > 0 ? extent * (bufferHeight / bufferWidth) : extent;
  return {
    x: (px / bufferWidth - 0.5) * worldWidth,
    z: (py / bufferHeight - 0.5) * worldHeight,
  };
}

/** Sample the wave field at a world point and time. `steepness` matches the render shaping. */
export function probeWorld(
  waves: readonly WaveParams[],
  world: Vec2,
  t: number,
  steepness = 0,
): ProbeResult {
  return {
    height: gerstnerHeight(waves, world, t, steepness),
    world,
    normal: gerstnerNormal(waves, world, t, steepness),
  };
}

/**
 * Sample the wave field at a drawing-buffer pixel. `px`/`py` are in buffer
 * pixels (CSS pixels multiplied by the device pixel ratio).
 */
export function probePixel(
  waves: readonly WaveParams[],
  px: number,
  py: number,
  bufferWidth: number,
  bufferHeight: number,
  extent: number,
  t: number,
  steepness = 0,
): ProbeResult {
  return probeWorld(waves, pixelToWorld(px, py, bufferWidth, bufferHeight, extent), t, steepness);
}
